import { useResizableState } from '@platejs/resizable'
import type { TElement } from 'platejs'
import { useEditorRef, useElement } from 'platejs/react'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { findElementPath } from '../hooks/use-element-path.js'

export interface ResizableBlockStateOptions {
  readOnly: boolean
  align?: 'left' | 'center' | 'right'
  minWidth: number | string
  maxWidth: number | string
}

/**
 * `useResizableState` with a `setNodeWidth` that resolves the element's path when the resize
 * finishes. Plate's own `setNodeWidth` writes to `usePath()`, which is memoised on the element
 * object: a block inserted or removed above (Enter on the line before an image) shifts the element
 * without changing it, so the stale path put `width` on a neighbouring block. The resizing client
 * still showed its local width, so the resize looked applied but never reached the image (GH #1953).
 */
export const useResizableBlockState = (options: ResizableBlockStateOptions) => {
  const editor = useEditorRef()
  const element = useElement<TElement & { width?: string | number }>()
  const state = useResizableState(options)
  const nodeWidth = element.width ?? '100%'

  const setNodeWidth = (width: number) => {
    const path = findElementPath(editor as unknown as TunaEditor, element)
    if (!path) {
      return
    }
    if (width === nodeWidth) {
      editor.tf.select(path)
      return
    }
    editor.tf.setNodes({ width }, { at: path })
  }

  return { ...state, setNodeWidth }
}
